import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success(t('contactPage.messageSent'));
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: t('enterprisesPage.email'),
      value: "contact@sfretire.com",
      href: "mailto:contact@sfretire.com"
    },
    {
      icon: Phone,
      label: t('contactPage.phone'),
      value: "+33 1 23 45 67 89",
      href: "tel:+33123456789"
    },
    {
      icon: MapPin,
      label: t('contactPage.address'),
      value: "Paris, France",
      href: null
    },
    {
      icon: Clock,
      label: t('contactPage.hours'),
      value: t('contactPage.hoursValue'),
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-6">
                {t('contactPage.title')}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t('contactPage.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-serif font-bold text-foreground mb-6">
                  {t('contactPage.sendMessage')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('contactPage.fullName')} *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={t('contactPage.yourName')}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground">{t('contactPage.email')} *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="votre@email.com"
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground">{t('contactPage.subject')} *</label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder={t('contactPage.subjectPlaceholder')}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground">{t('contactPage.message')} *</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={t('contactPage.messagePlaceholder')}
                      rows={5}
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    variant="gold" 
                    size="lg" 
                    className="w-full gap-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('contactPage.sending') : (
                      <>
                        {t('contactPage.send')}
                        <Send size={18} />
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-2xl font-serif font-bold text-foreground mb-6">
                  {t('contactPage.ourCoordinates')}
                </h2>

                <div className="space-y-4 mb-10">
                  {contactInfo.map((item) => {
                    const IconComponent = item.icon;
                    const content = (
                      <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors">
                        <div className="icon-circle shrink-0">
                          <IconComponent size={20} />
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">{item.label}</div>
                          <div className="font-medium text-foreground">{item.value}</div>
                        </div>
                      </div>
                    );

                    return item.href ? (
                      <a key={item.label} href={item.href} className="block">
                        {content}
                      </a>
                    ) : (
                      <div key={item.label}>{content}</div>
                    );
                  })}
                </div>

                {/* Additional Info Card */}
                <div className="bg-card rounded-xl p-6 border border-border/50">
                  <h3 className="font-serif font-semibold text-foreground mb-3">
                    {t('contactPage.businessQuestion')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('contactPage.businessDescription')}
                  </p>
                  <Button variant="outline-gold" size="sm">
                    {t('contactPage.requestDemo')}
                  </Button>
                </div>

                {/* FAQ Hint */}
                <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{t('contactPage.quickAnswer')}</span>
                    {' '}{t('contactPage.faqHint')}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
